import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DollarSign, TrendingUp, Wallet, Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get stored device tokens
const getDeviceTokens = () => {
  try {
    const tokens = localStorage.getItem('device_tokens');
    return tokens ? JSON.parse(tokens) : {};
  } catch {
    return {};
  }
};

// Helper to store device token for an email
const storeDeviceToken = (email, deviceToken) => {
  const tokens = getDeviceTokens();
  tokens[email.toLowerCase()] = deviceToken;
  localStorage.setItem('device_tokens', JSON.stringify(tokens));
};

export default function Login() {
  const navigate = useNavigate();
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);

  const handleGoogleLogin = () => {
    // Store remember me preference for use after OAuth callback
    sessionStorage.setItem('remember_me', rememberMe ? 'true' : 'false');
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error('Please enter your email');
      return;
    }

    // Store remember me preference
    sessionStorage.setItem('remember_me', rememberMe ? 'true' : 'false');

    setLoading(true);
    
    // First, try device token login (for returning users on same browser)
    const deviceTokens = getDeviceTokens();
    const deviceToken = deviceTokens[email.toLowerCase()];
    
    if (deviceToken) {
      try {
        const response = await fetch(`${API}/auth/device-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, device_token: deviceToken })
        });

        if (response.ok) {
          const data = await response.json();
          
          // Store session token
          if (rememberMe) {
            localStorage.setItem('session_token', data.session_token);
          } else {
            sessionStorage.setItem('session_token', data.session_token);
          }
          
          toast.success('Welcome back!');
          navigate('/dashboard');
          return;
        }
        // If device login fails, fall through to magic link
      } catch (error) {
        console.log('Device login failed, falling back to magic link');
      }
    }

    // Fall back to magic link for new devices or if device token is invalid
    try {
      const response = await fetch(`${API}/auth/magic-link/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name: name || undefined })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to send login link');
      }

      setEmailSent(true);
      toast.success('Login link sent! Check your email.');
    } catch (error) {
      toast.error(error.message || 'Failed to send login link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex relative">
      {/* Background image - more faded overlay for better text legibility */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: 'url(https://kilobeaters.com/cdn/shop/files/Desktop_banner.jpg?v=1767938597&width=3840)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-white/90 dark:bg-black/90"></div>
      </div>

      {/* Left side - Feature highlights (desktop only) */}
      <div className="hidden lg:flex lg:w-1/2 relative z-10 items-center justify-center p-12">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-md space-y-8"
        >
          <div>
            <h2 className="text-3xl xl:text-4xl font-bold mb-4 text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Earn With Every Referral
            </h2>
            <p className="text-gray-600 text-lg xl:text-xl">
              Join the KiloBeaters partner program and turn your network into revenue
            </p>
          </div>
          
          {/* Feature highlights */}
          <div className="space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-lg">Up to 25% Commission</p>
                <p className="text-base text-gray-500">Tiered rewards based on performance</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-lg">Equity Linked Instruments</p>
                <p className="text-base text-gray-500">Allocated as a percentage of revenue earned</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Wallet className="w-7 h-7 text-orange-500" />
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-lg">Fixed Monthly Retainer</p>
                <p className="text-base text-gray-500">For Gold and Platinum partners</p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md space-y-8 bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-8 rounded-2xl shadow-xl"
        >
          <div className="text-center space-y-2">
            <img
              src="https://customer-assets.emergentagent.com/job_agent-earnings-5/artifacts/95ok324x_KB%20-%20Final%20Logo%20%28Updated%29-01.png"
              alt="KILO BEATERS"
              className="h-20 lg:h-24 mx-auto mb-6"
            />
            <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-gray-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-gray-600 text-base lg:text-lg">
              Sign in to your partner account
            </p>
          </div>

          {!showEmailLogin ? (
            // Main login options
            <div className="space-y-4 mt-8">
              <Button
                data-testid="google-login-button"
                onClick={handleGoogleLogin}
                size="lg"
                className="w-full h-12 text-base font-medium bg-orange-500 hover:bg-orange-600 text-white transition-all duration-300"
              >
                <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#fff"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff"/>
                </svg>
                Sign in with Google
              </Button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white/80 text-gray-500">or</span>
                </div>
              </div>

              <Button
                data-testid="email-login-button"
                onClick={() => setShowEmailLogin(true)}
                variant="outline"
                size="lg"
                className="w-full h-12 text-base font-medium border-gray-300 hover:bg-gray-50 transition-all duration-300"
              >
                <Mail className="w-5 h-5 mr-3" />
                Sign in with Email
              </Button>

              <div className="flex items-center space-x-2 justify-center mt-4">
                <Checkbox 
                  id="remember-me" 
                  checked={rememberMe}
                  onCheckedChange={setRememberMe}
                  data-testid="remember-me-checkbox"
                />
                <label 
                  htmlFor="remember-me" 
                  className="text-sm text-gray-600 cursor-pointer select-none"
                >
                  Remember me on this device
                </label>
              </div>

              <p className="text-xs text-center text-gray-500 mt-4">
                By continuing, you agree to our Terms of Service and Privacy Policy
              </p>
            </div>
          ) : emailSent ? (
            // Email sent confirmation
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="space-y-6 mt-8"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-2">Check your email!</h2>
                <p className="text-gray-600">
                  We've sent a login link to<br />
                  <strong className="text-gray-800">{email}</strong>
                </p>
              </div>

              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="text-sm text-orange-800">
                  <strong>Tip:</strong> The link expires in 15 minutes. Check your spam folder if you don't see it.
                </p>
              </div>

              <Button
                variant="ghost"
                onClick={() => {
                  setShowEmailLogin(false);
                  setEmailSent(false);
                  setEmail('');
                  setName('');
                }}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to login options
              </Button>
            </motion.div>
          ) : (
            // Email login form
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-4 mt-8"
            >
              <Button
                variant="ghost"
                onClick={() => setShowEmailLogin(false)}
                className="mb-2 -ml-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email address</label>
                  <Input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-12"
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Your name <span className="text-gray-400">(optional)</span></label>
                  <Input
                    type="text"
                    placeholder="John Doe"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12"
                    data-testid="name-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading || !email}
                  size="lg"
                  className="w-full h-12 text-base font-medium bg-orange-500 hover:bg-orange-600 text-white transition-all duration-300"
                  data-testid="send-magic-link-button"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    <>
                      <Mail className="w-5 h-5 mr-2" />
                      Send Login Link
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-center text-gray-500">
                We'll email you a magic link for password-free login
              </p>
            </motion.div>
          )}

          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl lg:text-3xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>15%</div>
                <div className="text-xs lg:text-sm text-gray-500">Starting Commission</div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>24/7</div>
                <div className="text-xs lg:text-sm text-gray-500">Support</div>
              </div>
              <div>
                <div className="text-2xl lg:text-3xl font-bold text-orange-500" style={{ fontFamily: 'Outfit, sans-serif' }}>Fast</div>
                <div className="text-xs lg:text-sm text-gray-500">Payouts</div>
              </div>
            </div>
          </div>

          {/* Link to Signup */}
          <div className="text-center pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className="font-semibold text-orange-500 hover:text-orange-600 transition-colors"
              >
                Sign up
              </Link>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              <Link 
                to="/benefits" 
                className="text-orange-500 hover:text-orange-600 transition-colors underline"
              >
                View Partner Benefits & Tiers →
              </Link>
            </p>
          </div>

          {/* Mobile-only feature highlights */}
          {!showEmailLogin && (
            <div className="lg:hidden mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center mb-4">Why join KiloBeaters?</p>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50">
                  <DollarSign className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">Up to 25% commission on referrals</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50">
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">Equity linked instruments based on revenue</span>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-orange-50">
                  <Wallet className="w-5 h-5 text-orange-500" />
                  <span className="text-sm text-gray-700">Fixed monthly retainer for Gold & Platinum</span>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
